import { Link, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { Button, Callout, Field, inputClass } from '@/components/ui';
import { resetIdentity } from '@/query-client';
import { AFTER_SIGN_IN } from '@/storefront';

/**
 * The rule, in the words a person reads. The SERVER decides, using the same
 * rule in `server/handlers/password-reset.ts`, and the server's copy is checked
 * before the reset token is spent.
 */
const MIN_PASSWORD = 8;

const CLASSES: Array<{ chars: string; label: string }> = [
  { chars: 'abcdefghijklmnopqrstuvwxyz', label: 'a lower case letter' },
  { chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ', label: 'a capital letter' },
  { chars: '0123456789', label: 'a number' },
];

function passwordProblem(password: string): string | null {
  if (password.length < MIN_PASSWORD) return `Use at least ${MIN_PASSWORD} characters.`;
  const missing = CLASSES
    .filter((c) => ![...password].some((ch) => c.chars.includes(ch)))
    .map((c) => c.label);
  if (missing.length === 0) return null;
  if (missing.length === 1) return `Add ${missing[0]}.`;
  return `Add ${missing.slice(0, -1).join(', ')} and ${missing.at(-1)}.`;
}

/**
 * Choose a new password, using the link from the email.
 *
 * The token is in the query string rather than the URL fragment on purpose: the
 * server exchanges it, so it has to reach the server. See the comment in
 * `server/handlers/password-reset.ts` about why this does not use Supabase's
 * own `action_link`.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token ?? '';

  const [errors, setErrors] = useState<{ password?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  // A link that arrived without a token cannot be used, and saying so here
  // beats a form that collects a password and then fails.
  if (!token) {
    return (
      <div className="mx-auto max-w-[440px] px-5 py-16">
        <h1 className="text-[32px] font-semibold tracking-[-0.02em]">That link is incomplete</h1>
        <p className="mt-2 text-[15px] text-muted">
          Some email apps cut long links in half. Ask for a new one and open it from the
          email rather than copying it.
        </p>
        <p className="mt-8 text-[15px]">
          <Link to="/auth/forgot" className="font-semibold underline">
            Send a new link
          </Link>
        </p>
      </div>
    );
  }

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const password = String(form.get('password') ?? '');
    const confirm = String(form.get('confirm') ?? '');

    // Checked here so a rule the person can fix never reaches the server, and
    // so never spends the single-use link on a password that was going to be
    // refused anyway.
    const problem = passwordProblem(password);
    if (problem) {
      setErrors({ password: problem });
      return;
    }
    // Checked here and nowhere else: the server never sees the second field,
    // because a typo in a password nobody can read is a lockout, not an attack.
    if (password !== confirm) {
      setErrors({ password: 'Those two do not match.' });
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const res = await fetch('/api/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } }
          | null;
        setErrors({ form: body?.error?.message ?? 'That did not go through. Try again.' });
        return;
      }
      // The verify signed them in, so the cached signed-out identity is stale.
      // Same ordering as sign-in: drop it BEFORE navigating or the admin gate
      // reads the old answer and bounces them back (R-99).
      resetIdentity();
      await router.invalidate();
      await navigate({ to: AFTER_SIGN_IN as never });
    } catch {
      setErrors({ form: 'That did not go through. Check your connection.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-[440px] px-5 py-16">
      <h1 className="text-[32px] font-semibold tracking-[-0.02em]">Choose a new password</h1>
      <p className="mt-2 text-[15px] text-muted">
        At least {MIN_PASSWORD} characters, with a capital letter, a lower case letter
        and a number. You will be signed in once it is saved.
      </p>

      <form onSubmit={submit} noValidate className="mt-8 space-y-6">
        {errors.form && <Callout tone="danger" title={errors.form} />}

        <Field label="New password" htmlFor="password" error={errors.password}>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            className={inputClass}
            aria-invalid={Boolean(errors.password)}
          />
        </Field>

        <Field label="New password again" htmlFor="confirm">
          <input
            id="confirm"
            name="confirm"
            type="password"
            autoComplete="new-password"
            className={inputClass}
          />
        </Field>

        <Button type="submit" size="lg" disabled={submitting}>
          {submitting ? 'Saving' : 'Save and sign in'}
        </Button>

        <p className="text-[15px] text-muted">
          <Link to="/auth/sign-in" className="font-semibold underline">
            Back to sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
